import React from "react";
import { Plus, X, CalendarDays } from "lucide-react";
import { FullWidth } from "./BookingFormFields";

const ENTRY_TYPES = ["Performance", "Rehearsal", "Tech / Build", "Other"];

function emptyEntry() {
  return { type: "Performance", date: "", start_time: "", end_time: "", notes: "" };
}

/**
 * Repeating list of date entries. Each row: type / date / start / end / notes / remove.
 * Used to capture rehearsal + performance schedules in one place.
 */
export default function ScheduleField({
  label = "Schedule",
  hint = "Add a row for each performance, rehearsal, tech day, or other booking date.",
  value = [],
  onChange,
  testId = "schedule",
}) {
  const rows = value.length > 0 ? value : [emptyEntry()];

  const update = (idx, patch) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };
  const add = () => onChange([...rows, emptyEntry()]);
  const remove = (idx) => {
    const next = rows.filter((_, i) => i !== idx);
    onChange(next.length > 0 ? next : [emptyEntry()]);
  };

  const inputCls =
    "w-full bg-stone-950 border border-white/10 rounded-md px-3 py-2.5 text-stone-100 font-body text-sm placeholder:text-stone-600 focus:border-[#DE9E48] focus:outline-none transition-colors";

  return (
    <FullWidth>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
        <p className="font-overline text-[0.65rem] text-stone-400 tracking-[0.18em]">
          {label}
        </p>
        <span className="font-overline text-[0.6rem] text-stone-500">
          {rows.length} {rows.length === 1 ? "date" : "dates"}
        </span>
      </div>
      <p className="font-body text-[0.78rem] text-stone-500 mb-4 leading-relaxed">{hint}</p>

      <div className="space-y-4" data-testid={testId}>
        {rows.map((row, idx) => (
          <div
            key={idx}
            data-testid={`${testId}-row-${idx}`}
            className="border border-white/10 rounded-xl p-4 bg-stone-900/30 relative"
          >
            <div className="flex items-center gap-3 mb-3">
              <CalendarDays size={14} className="text-[#DE9E48]" />
              <span className="font-overline text-[0.65rem] text-[#DE9E48] tracking-[0.18em]">
                Date {idx + 1}
              </span>
              {rows.length > 1 && (
                <button
                  type="button"
                  data-testid={`${testId}-remove-${idx}`}
                  onClick={() => remove(idx)}
                  aria-label="Remove this date"
                  className="ml-auto w-7 h-7 rounded-full flex items-center justify-center text-stone-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="font-overline text-[0.6rem] text-stone-400 block mb-1.5">
                  Type
                </span>
                <select
                  data-testid={`${testId}-type-${idx}`}
                  value={row.type}
                  onChange={(e) => update(idx, { type: e.target.value })}
                  className={inputCls}
                >
                  {ENTRY_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="font-overline text-[0.6rem] text-stone-400 block mb-1.5">
                  Date
                </span>
                <input
                  data-testid={`${testId}-date-${idx}`}
                  type="text"
                  value={row.date}
                  onChange={(e) => update(idx, { date: e.target.value })}
                  placeholder="e.g. Aug 15, 2026"
                  className={inputCls}
                />
              </label>

              <label className="block">
                <span className="font-overline text-[0.6rem] text-stone-400 block mb-1.5">
                  Start Time
                </span>
                <input
                  data-testid={`${testId}-start-${idx}`}
                  type="text"
                  value={row.start_time}
                  onChange={(e) => update(idx, { start_time: e.target.value })}
                  placeholder="e.g. 7:30 PM"
                  className={inputCls}
                />
              </label>

              <label className="block">
                <span className="font-overline text-[0.6rem] text-stone-400 block mb-1.5">
                  End Time
                </span>
                <input
                  data-testid={`${testId}-end-${idx}`}
                  type="text"
                  value={row.end_time}
                  onChange={(e) => update(idx, { end_time: e.target.value })}
                  placeholder="e.g. 10:00 PM"
                  className={inputCls}
                />
              </label>

              <label className="block sm:col-span-2">
                <span className="font-overline text-[0.6rem] text-stone-400 block mb-1.5">
                  Notes (optional)
                </span>
                <input
                  data-testid={`${testId}-notes-${idx}`}
                  type="text"
                  value={row.notes}
                  onChange={(e) => update(idx, { notes: e.target.value })}
                  placeholder="e.g. Opening night, dress rehearsal, load-in"
                  className={inputCls}
                />
              </label>
            </div>
          </div>
        ))}

        <button
          type="button"
          data-testid={`${testId}-add`}
          onClick={add}
          className="w-full border border-dashed border-white/15 rounded-xl py-3.5 font-overline text-xs text-stone-300 hover:border-[#DE9E48] hover:text-[#DE9E48] transition-colors inline-flex items-center justify-center gap-2"
        >
          <Plus size={14} /> Add Another Date
        </button>
      </div>
    </FullWidth>
  );
}
